import React from "react";
import MrclText from "../marcel/MrclText";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { formatCurrency, formatPercentage, getCurrencySuffix } from "../../utils/numberUtils";
import { InfoBull } from "../InfoBull";
import DeltaBadge from "../DeltaBadge";

type CommercialCardDesktopProps = {
  title: string;
  subTitle?: string;
  turnover: number;
  turnoverTotal?: string;
  importedDateTime?: string;
  rhRate: number;
  rhRateCurrency?: string;
  currency?: string;
  isUnavailable: boolean;
  isLoading: boolean;
  messageError?: string;
  openInformationModal: (title?: string) => void;
};

const CommercialCardDesktop = ({
  title,
  subTitle,
  turnover,
  rhRate,
  rhRateCurrency,
  currency,
  turnoverTotal,
  importedDateTime,
  isUnavailable,
  isLoading,
  messageError,
  ...res
}: CommercialCardDesktopProps) => {
  const {t} = useTranslation();

  const getFormattedValue = () => {
    if (!turnover || isNaN(turnover)) return null;
    
    switch (currency) {
      case "€":
        return formatCurrency(turnover, "");
      case "k":
        return turnover >= 1000 ? formatCurrency(turnover, "") : turnover;
      case "%":
        return formatPercentage(turnover, "");
      default:
        return turnover;
    }
  };

  const getSuffix = () => {
    if (currency === "€") {
      return getCurrencySuffix(turnover / 1000);
    }
    if (currency === "%") {
      return "%";
    }
    if (currency === "k" && turnover >= 1000) {
      return "k";
    }
    return "";
  };

  return (
        <Card>
          <HeaderContainer>
            <CTextHeader>{title}</CTextHeader>
            <InfoBullWrapper>
              <InfoBull
                  description={title as string}
                  lastUpdated={importedDateTime}
                  loading={isLoading}
                  isUnavailable={isUnavailable}
                  onOpenTracking={() => {}}
              />
            </InfoBullWrapper>
          </HeaderContainer>
          <Content>
          <CTextValue>
            {getFormattedValue() !== null ? `${getFormattedValue()}${getSuffix()}` : "-"}
          </CTextValue>
      <TurnoverTotalWrapper>
        {turnoverTotal && (
          <CTextValue
            $fontSize={".7rem"}
            $color="#575757"
          >
            {turnoverTotal}
          </CTextValue>
        )}
      </TurnoverTotalWrapper>
          <Variation>
            {rhRate != null && !isNaN(rhRate) ? (
              <>
                <DeltaBadge value={rhRate} suffix={rhRateCurrency ?? "%"} />
                <span className="vs">vs {t("commercial.n_1")}</span>
              </>
            ) : (
              "-"
            )}
          </Variation>
          </Content>
        </Card>
  );
};

export default CommercialCardDesktop;

const Card = styled.div`
  background: #fff;
  border-radius: 8px;
  box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.05);
  padding: 16px;
  width: 100%;
  height: 100%;
  min-height: 130px;
  box-sizing: border-box;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  transition: transform 0.15s ease-in-out;

  @media only screen and (max-height: 560px) and (min-width: 568px) and (orientation: landscape) {
    padding: 8px;
  }
`;

const InfoBullWrapper = styled.div`
  margin-right: 4px;
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
`;

const TurnoverTotalWrapper = styled.div`
  min-height: 14px;
  display: flex;
  align-items: center;
`;

const CTextHeader = styled(MrclText)`
  color: #121212;
  font-size: var(--font-size-body-m, 16px);
  font-weight: 700;
  margin-bottom: 4px;

  @media only screen and (max-height: 560px) and (min-width: 568px) and (orientation: landscape) {
    font-size: 11px;
  }
`;

const CTextValue = styled(MrclText)<{$fontSize?: string, $color?: string}>`
  color: ${(props) => props.$color || "#0c1021"};
  font-size: ${(props) => props.$fontSize || "24px"} !important;
  font-weight: 700;
  margin: 0;
  line-height: 1.1;
  text-align: left;

  @media only screen and (max-height: 560px) and (min-width: 568px) and (orientation: landscape) {
    font-size: ${(props) => (props.$fontSize ? props.$fontSize : "18px")} !important;
  }
`;

const Variation = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 0;

  .vs {
    color: #9a9da6;
    font-size: 12px;
    line-height: 1;
  }

  @media only screen and (max-height: 560px) and (min-width: 568px) and (orientation: landscape) {
    gap: 4px;

    .vs {
      font-size: 10px;
    }
  }
`;
const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  width: 100%;
  gap: 6px;
  margin-top: 0;

  @media only screen and (max-height: 560px) and (min-width: 568px) and (orientation: landscape) {
    gap: 4px;
  }
`;
